import { useState, useEffect, useRef, useCallback } from "react";
import nftMrktHeroImg1 from "../../../image/NFTMarketplaceNew/nftMrktHeroImg1.webp"
import nftMrktHeroImg2 from "../../../image/NFTMarketplaceNew/nftMrktHeroImg2.webp"
import nftMrktHeroImg3 from "../../../image/NFTMarketplaceNew/nftMrktHeroImg3.webp"
import nftMrktHeroImg4 from "../../../image/NFTMarketplaceNew/nftMrktHeroImg4.webp"
import nftMrktHeroImg5 from "../../../image/NFTMarketplaceNew/nftMrktHeroImg5.webp"
import nftMrktHeroImg6 from "../../../image/NFTMarketplaceNew/nftMrktHeroImg6.webp"
import nftMrktHeroImg7 from "../../../image/NFTMarketplaceNew/nftMrktHeroImg7.webp"
import nftMrktHeroImg8 from "../../../image/NFTMarketplaceNew/nftMrktHeroImg8.webp"
import nftMrktHeroImg9 from "../../../image/NFTMarketplaceNew/nftMrktHeroImg9.webp"
import nftMrktHeroImg10 from "../../../image/NFTMarketplaceNew/nftMrktHeroImg10.webp"
import nftMrktHeroImgPrev from "../../../image/NFTMarketplaceNew/nftMrktHeroImgPrev.webp"
import nftMrktHeroImgNext from "../../../image/NFTMarketplaceNew/nftMrktHeroImgNext.webp"

const DATA = [
  { img: nftMrktHeroImg1 },
  { img: nftMrktHeroImg2 },
  { img: nftMrktHeroImg3 },
  { img: nftMrktHeroImg4 },
  { img: nftMrktHeroImg5 },
  { img: nftMrktHeroImg6 },
  { img: nftMrktHeroImg7 },
  { img: nftMrktHeroImg8 },
  { img: nftMrktHeroImg9 },
  { img: nftMrktHeroImg10 },
  { img: nftMrktHeroImg1 },
  { img: nftMrktHeroImg2 },
  { img: nftMrktHeroImg3 },
  { img: nftMrktHeroImg4 },
  { img: nftMrktHeroImg5 },
  { img: nftMrktHeroImg6 },
  { img: nftMrktHeroImg7 },
  { img: nftMrktHeroImg8 },
];

const N = DATA.length;
const STEP = (2 * Math.PI) / N; // 20° per card

// ── Card sizes (design px, inside the logical canvas) ──────────────────
const ACT_W = 165, ACT_H = 220;   // active card
const INA_W = 158, INA_H = 212;   // inactive card
const GAP   = 32;                  // gap between card edges (arc-length)

// ── Orbit radius derived so arc between centres = card width + gap ──────
const ORBIT_R = (INA_W + GAP) / STEP;   // ≈ 544 px

// ── Logical canvas — large enough to show every card fully ──────────────
const HALF_DIAG  = Math.sqrt((ACT_W / 2) ** 2 + (ACT_H / 2) ** 2); // ≈ 137
const CANVAS     = Math.ceil((ORBIT_R + HALF_DIAG + 32) * 2);        // ≈ 1426
const CX = CANVAS / 2;
const CY = CANVAS / 2;

// ── Timing ──────────────────────────────────────────────────────────────
const PAUSE_MS = 3000;
const SNAP_MS  = 700;

function easeInOut(t) {
  return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
}
function normalizeAngle(a) {
  a = a % (2 * Math.PI);
  if (a < 0) a += 2 * Math.PI;
  return a;
}
function shortestRotation(idx, cur) {
  const base = -idx * STEP;
  let diff = base - (cur % (2 * Math.PI));
  while (diff >  Math.PI) diff -= 2 * Math.PI;
  while (diff < -Math.PI) diff += 2 * Math.PI;
  return cur + diff;
}

const CircularCarousel = () => {
  const wrapperRef      = useRef(null);
  const rotationRef     = useRef(0);
  const rafRef          = useRef(null);
  const resizeRafRef    = useRef(null); // NEW: separate raf id for the resize debounce
  const phaseRef        = useRef("snap");
  const activeIdxRef    = useRef(0);
  const phaseStartMsRef = useRef(null);
  const snapStartRef    = useRef(0);
  const snapEndRef      = useRef(0);
  const isManualRef     = useRef(false);

  const [activeIdx,  setActiveIdx]  = useState(0);
  const [scale,      setScale]      = useState(1);
  const [cardStates, setCardStates] = useState(() =>
    DATA.map(() => ({ x: CX, y: CY, tilt: 0, z: 1, isActive: false }))
  );

  // ── Scale canvas to fit wrapper width (debounced via rAF to avoid
  //    "ResizeObserver loop completed with undelivered notifications") ──
  useEffect(() => {
    function updateScale() {
      if (!wrapperRef.current) return;
      const w = wrapperRef.current.offsetWidth;
      const next = Math.min(w / CANVAS, 1);
      setScale(prev => (prev === next ? prev : next));
    }

    updateScale();

    const ro = new ResizeObserver(() => {
      if (resizeRafRef.current) cancelAnimationFrame(resizeRafRef.current);
      resizeRafRef.current = requestAnimationFrame(updateScale);
    });

    if (wrapperRef.current) ro.observe(wrapperRef.current);

    return () => {
      ro.disconnect();
      if (resizeRafRef.current) cancelAnimationFrame(resizeRafRef.current);
    };
  }, []);

  // ── Card state computation (always uses fixed CANVAS coords) ────────
  const computeStates = useCallback((rotation) => {
    return DATA.map((_, i) => {
      const angle    = i * STEP + rotation;
      const x        = CX + ORBIT_R * Math.sin(angle);
      const y        = CY - ORBIT_R * Math.cos(angle);
      const tilt     = angle * (180 / Math.PI);
      const normA    = normalizeAngle(angle);
      const dist     = Math.min(normA, 2 * Math.PI - normA);
      const isActive = dist < STEP * 0.55;
      const z        = isActive ? 500 : Math.round((Math.sin(angle) + 1) * 50 + 1);
      return { x, y, tilt, z, isActive };
    });
  }, []);

  // ── Machine ─────────────────────────────────────────────────────────
  const runMachine = useCallback((fromIdx) => {
    cancelAnimationFrame(rafRef.current);
    isManualRef.current     = false;
    activeIdxRef.current    = fromIdx;
    snapStartRef.current    = rotationRef.current;
    snapEndRef.current      = shortestRotation(fromIdx, rotationRef.current);
    phaseRef.current        = "snap";
    phaseStartMsRef.current = null;

    function tick() {
      if (isManualRef.current) return;
      const now = performance.now();
      if (phaseStartMsRef.current === null) phaseStartMsRef.current = now;
      const elapsed = now - phaseStartMsRef.current;

      if (phaseRef.current === "snap") {
        const t    = Math.min(elapsed / SNAP_MS, 1);
        const ease = easeInOut(t);
        rotationRef.current =
          snapStartRef.current + (snapEndRef.current - snapStartRef.current) * ease;
        setCardStates(computeStates(rotationRef.current));
        setActiveIdx(activeIdxRef.current);
        if (t >= 1) {
          rotationRef.current     = snapEndRef.current;
          phaseRef.current        = "pause";
          phaseStartMsRef.current = null;
        }
      } else {
        if (Math.min(elapsed / PAUSE_MS, 1) >= 1) {
          const ni                = (activeIdxRef.current + 1) % N;
          activeIdxRef.current    = ni;
          snapStartRef.current    = rotationRef.current;
          snapEndRef.current      = shortestRotation(ni, rotationRef.current);
          phaseRef.current        = "snap";
          phaseStartMsRef.current = null;
        }
      }
      rafRef.current = requestAnimationFrame(tick);
    }
    rafRef.current = requestAnimationFrame(tick);
  }, [computeStates]);

  const navTo = useCallback((idx) => {
    isManualRef.current = true;
    cancelAnimationFrame(rafRef.current);
    const startRot = rotationRef.current;
    const endRot   = shortestRotation(idx, startRot);
    const t0       = performance.now();

    function snap() {
      const t    = Math.min((performance.now() - t0) / SNAP_MS, 1);
      rotationRef.current = startRot + (endRot - startRot) * easeInOut(t);
      setCardStates(computeStates(rotationRef.current));
      setActiveIdx(idx);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(snap);
      } else {
        rotationRef.current     = endRot;
        activeIdxRef.current    = idx;
        phaseRef.current        = "pause";
        phaseStartMsRef.current = null;
        isManualRef.current     = false;
        const ps = performance.now();
        function pauseTick() {
          if (Math.min((performance.now() - ps) / PAUSE_MS, 1) >= 1) {
            runMachine((idx + 1) % N);
          } else {
            rafRef.current = requestAnimationFrame(pauseTick);
          }
        }
        rafRef.current = requestAnimationFrame(pauseTick);
      }
    }
    rafRef.current = requestAnimationFrame(snap);
  }, [computeStates, runMachine]);

  const handlePrev = useCallback(() => navTo(((activeIdxRef.current - 1) + N) % N), [navTo]);
  const handleNext = useCallback(() => navTo((activeIdxRef.current + 1) % N), [navTo]);

  useEffect(() => {
    setCardStates(computeStates(rotationRef.current));
    runMachine(0);
    return () => cancelAnimationFrame(rafRef.current);
  }, [computeStates, runMachine]);

  const activeData = DATA[activeIdx];

  return (
    <div
      ref={wrapperRef}
      style={{
        borderRadius: 16,
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: CANVAS * scale,
        overflow: "hidden",
        fontFamily: "system-ui, sans-serif",
        userSelect: "none",
      }}
    >
      <div
        style={{
          position: "relative",
          width:  CANVAS,
          height: CANVAS,
          flexShrink: 0,
          transform: `scale(${scale})`,
          transformOrigin: "center",
          left: '-31px',
        }}
      >
        {DATA.map((d, i) => {
          const s  = cardStates[i];
          const cw = s.isActive ? ACT_W : INA_W;
          const ch = s.isActive ? ACT_H : INA_H;
          return (
            <div
              key={i}
              onClick={() => navTo(i)}
              style={{
                position: "absolute",
                left: 0,
                top:  0,
                width:  cw,
                height: ch,
                borderRadius: 18,
                overflow: "hidden",
                cursor: "pointer",
                zIndex: s.z,
                transform: `translate(${s.x - cw / 2}px, ${s.y - ch / 2}px) rotate(${s.tilt}deg)`,
                transition: [
                  "width 0.35s cubic-bezier(0.34,1.56,0.64,1)",
                  "height 0.35s cubic-bezier(0.34,1.56,0.64,1)",
                  "box-shadow 0.35s ease",
                ].join(", "),
                margin: "0px 40px",
                padding: "0px 10px"
              }}
            >
              <img src={d.img} />
            </div>
          );
        })}

        <div 
        className="carulBtnWrp"
        style={{
          position: "absolute",
          top:  "410px",
          left: "54%",
          transform: "translate(-50%, -50%)",
          display: "flex", flexDirection: "row",
          alignItems: "center", gap: 25,
          zIndex: 999,
        }}>

          <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
            <NavBtn onClick={handlePrev} icon={nftMrktHeroImgPrev} label="Previous" accent={activeData.accent} />
          </div>

          <button
            style={{
              border: "none", borderRadius: 999,
              color: "#fff", fontSize: 13, fontWeight: 600,
              padding: "11px 28px", cursor: "pointer",
              whiteSpace: "nowrap",
              boxShadow: "0 0 28px rgba(168,85,247,0.45)",
            }}
            onMouseEnter={e => (e.currentTarget.style.opacity = "0.85")}
            onMouseLeave={e => (e.currentTarget.style.opacity = "1")}
            className="getFreeConstlBtn"
            data-toggle="modal" data-target="#myModal"
          >
            Get Free Consultation
          </button>

          <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
            <NavBtn onClick={handleNext} icon={nftMrktHeroImgNext} label="Next" accent={activeData.accent} />
          </div>

        </div>
      </div>
    </div>
  )
}

export default CircularCarousel


function NavBtn({ onClick, icon, label, accent }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      aria-label={label}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: "none",
        border: `none`,
        color: hovered ? "#fff" : accent,
        borderRadius: "50%",
        width: 34, height: 34,
        display: "flex", alignItems: "center", justifyContent: "center",
        cursor: "pointer", fontSize: 20, lineHeight: 1,
        transition: "background 0.2s, color 0.2s, border-color 0.4s",
      }}
    >
      <img src={icon} />
    </button>
  );
}